import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Bell, AlertTriangle, CheckCircle, Info, Clock, User, Calendar, Pill, DollarSign, CreditCard, FileText } from 'lucide-react';
import apiService from '../apiService';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

// Notification types and their configurations
const NOTIFICATION_TYPES = {
    APPOINTMENT_REMINDER: {
        icon: Calendar,
        color: 'bg-blue-100 text-blue-800',
        iconColor: 'text-blue-500'
    },
    PATIENT_WAITING: {
        icon: User,
        color: 'bg-yellow-100 text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    PRESCRIPTION_READY: {
        icon: Pill,
        color: 'bg-green-100 text-green-800',
        iconColor: 'text-green-500'
    },
    URGENT_ALERT: {
        icon: AlertTriangle,
        color: 'bg-red-100 text-red-800',
        iconColor: 'text-red-500'
    },
    SYSTEM_INFO: {
        icon: Info,
        color: 'bg-gray-100 text-gray-800',
        iconColor: 'text-gray-500'
    },
    SUCCESS: {
        icon: CheckCircle,
        color: 'bg-green-100 text-green-800',
        iconColor: 'text-green-500'
    },
    // Billing notifications
    INVOICE_CREATED: {
        icon: FileText,
        color: 'bg-blue-100 text-blue-800',
        iconColor: 'text-blue-500'
    },
    PAYMENT_DUE: {
        icon: Clock,
        color: 'bg-yellow-100 text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    PAYMENT_RECEIVED: {
        icon: CheckCircle,
        color: 'bg-green-100 text-green-800',
        iconColor: 'text-green-500'
    },
    PAYMENT_OVERDUE: {
        icon: AlertTriangle,
        color: 'bg-red-100 text-red-800',
        iconColor: 'text-red-500'
    },
    ACCOUNT_SUSPENDED: {
        icon: AlertTriangle,
        color: 'bg-red-100 text-red-800',
        iconColor: 'text-red-500'
    },
    ACCOUNT_REACTIVATED: {
        icon: CheckCircle,
        color: 'bg-green-100 text-green-800',
        iconColor: 'text-green-500'
    },
    TRIAL_EXPIRING: {
        icon: Clock,
        color: 'bg-yellow-100 text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    TRIAL_EXPIRED: {
        icon: AlertTriangle,
        color: 'bg-orange-100 text-orange-800',
        iconColor: 'text-orange-500'
    },
    UPGRADE_REQUEST_CREATED: {
        icon: DollarSign,
        color: 'bg-blue-100 text-blue-800',
        iconColor: 'text-blue-500'
    },
    UPGRADE_REQUEST_APPROVED: {
        icon: CheckCircle,
        color: 'bg-green-100 text-green-800',
        iconColor: 'text-green-500'
    },
    UPGRADE_REQUEST_REJECTED: {
        icon: AlertTriangle,
        color: 'bg-red-100 text-red-800',
        iconColor: 'text-red-500'
    },
    MODULE_ACTIVATED: {
        icon: CheckCircle,
        color: 'bg-green-100 text-green-800',
        iconColor: 'text-green-500'
    },
    ORGANIZATION_CREATED: {
        icon: CheckCircle,
        color: 'bg-green-100 text-green-800',
        iconColor: 'text-green-500'
    },
    ACCOUNT_REQUEST_RECEIVED: {
        icon: FileText,
        color: 'bg-blue-100 text-blue-800',
        iconColor: 'text-blue-500'
    },
    SYSTEM_ALERT: {
        icon: AlertTriangle,
        color: 'bg-red-100 text-red-800',
        iconColor: 'text-red-500'
    }
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotificationPanel, setShowNotificationPanel] = useState(false);
    const isFetchingRef = useRef(false);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);

    // Fetch notifications from backend with debounce protection
    const fetchNotifications = useCallback(async () => {
        // Prevent multiple simultaneous fetches
        if (isFetchingRef.current) {
            console.log('Notification fetch already in progress, skipping...');
            return;
        }
        
        isFetchingRef.current = true;
        try {
            const response = await apiService.getNotifications(false, 50);
            if (response.notifications) {
                // Convert backend notifications to frontend format
                const backendNotifications = response.notifications.map(n => ({
                    id: n.id,
                    type: n.type,
                    title: n.title,
                    message: n.message,
                    priority: n.priority,
                    read: n.read,
                    timestamp: new Date(n.createdAt),
                    actionUrl: n.actionUrl,
                    actionText: n.actionText,
                    persistent: true,
                    fromBackend: true
                }));
                
                setNotifications(backendNotifications);
                
                // Count unread
                const unread = backendNotifications.filter(n => !n.read).length;
                setUnreadCount(unread);
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            isFetchingRef.current = false;
        }
    }, []);

    // WebSocket connection setup
    const connectWebSocket = useCallback(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.log('No token available, skipping WebSocket connection');
            return;
        }

        try {
            // Determine WebSocket URL based on current environment
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const host = process.env.REACT_APP_API_URL 
                ? new URL(process.env.REACT_APP_API_URL).host 
                : 'localhost:8007';
            const wsUrl = `${protocol}//${host}/ws?token=${token}`;
            
            console.log('Connecting to WebSocket:', wsUrl);
            const ws = new WebSocket(wsUrl);
            
            ws.onopen = () => {
                console.log('✅ WebSocket connected - real-time notifications enabled');
                // Fetch initial notifications on connection
                fetchNotifications();
            };
            
            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    console.log('WebSocket message received:', data);
                    
                    if (data.type === 'notification' && data.notification) {
                        // Add new notification to the list
                        const newNotification = {
                            id: data.notification.id,
                            type: data.notification.type,
                            title: data.notification.title,
                            message: data.notification.message,
                            priority: data.notification.priority,
                            read: data.notification.read,
                            timestamp: new Date(data.notification.createdAt),
                            actionUrl: data.notification.actionUrl,
                            actionText: data.notification.actionText,
                            persistent: true,
                            fromBackend: true
                        };
                        
                        setNotifications(prev => [newNotification, ...prev]);
                        setUnreadCount(prev => prev + 1);
                        
                        // Show browser notification if permission granted
                        if (Notification.permission === 'granted') {
                            new Notification(newNotification.title, {
                                body: newNotification.message,
                                icon: '/favicon.ico',
                                tag: newNotification.id
                            });
                        }
                    } else if (data.type === 'connection_established') {
                        console.log('WebSocket connection established:', data.message);
                    } else if (data.type === 'pong') {
                        console.log('WebSocket ping response received');
                    }
                } catch (error) {
                    console.error('Failed to parse WebSocket message:', error);
                }
            };
            
            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };
            
            ws.onclose = (event) => {
                console.log('WebSocket disconnected:', event.code, event.reason);
                wsRef.current = null;
                
                // Attempt to reconnect after 5 seconds
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                }
                reconnectTimeoutRef.current = setTimeout(() => {
                    console.log('Attempting to reconnect WebSocket...');
                    connectWebSocket();
                }, 5000);
            };
            
            wsRef.current = ws;
        } catch (error) {
            console.error('Failed to create WebSocket connection:', error);
        }
    }, [fetchNotifications]);

    // Initialize: fetch notifications and connect WebSocket
    useEffect(() => {
        // Fetch initial notifications
        fetchNotifications();
        
        // Connect to WebSocket for real-time updates
        connectWebSocket();
        
        // Cleanup on unmount
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, [fetchNotifications, connectWebSocket]);

    // Add a new notification
    const addNotification = useCallback((notification) => {
        const newNotification = {
            id: Date.now() + Math.random(),
            timestamp: new Date(),
            read: false,
            ...notification
        };

        setNotifications(prev => [newNotification, ...prev]);
        setUnreadCount(prev => prev + 1);

        // Auto-remove after 10 seconds for non-persistent notifications
        if (!notification.persistent) {
            setTimeout(() => {
                removeNotification(newNotification.id);
            }, 10000);
        }

        // Show browser notification if permission granted
        if (Notification.permission === 'granted' && notification.showBrowserNotification !== false) {
            new Notification(notification.title, {
                body: notification.message,
                icon: '/favicon.ico',
                tag: notification.id
            });
        }
    }, []);

    // Remove a notification
    const removeNotification = useCallback((id) => {
        setNotifications(prev => {
            const notification = prev.find(n => n.id === id);
            if (notification && !notification.read) {
                setUnreadCount(count => Math.max(0, count - 1));
            }
            return prev.filter(n => n.id !== id);
        });
    }, []);

    // Mark notification as read
    const markAsRead = useCallback(async (id) => {
        // Update UI immediately
        setNotifications(prev => 
            prev.map(n => 
                n.id === id ? { ...n, read: true } : n
            )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        // Update backend if it's a backend notification
        const notification = notifications.find(n => n.id === id);
        if (notification && notification.fromBackend) {
            try {
                await apiService.markNotificationRead(id);
            } catch (error) {
                console.error('Failed to mark notification as read:', error);
            }
        }
    }, [notifications]);

    // Mark all notifications as read
    const markAllAsRead = useCallback(async () => {
        setNotifications(prev => 
            prev.map(n => ({ ...n, read: true }))
        );
        setUnreadCount(0);
        
        // Update backend
        try {
            await apiService.markAllNotificationsRead();
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    }, []);

    // Clear all notifications
    const clearAll = useCallback(() => {
        setNotifications([]);
        setUnreadCount(0);
    }, []);

    // Request notification permission
    const requestNotificationPermission = useCallback(async () => {
        if ('Notification' in window && Notification.permission === 'default') {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }
        return Notification.permission === 'granted';
    }, []);

    // Convenience methods for common notification types
    const notifyAppointmentReminder = useCallback((appointment) => {
        addNotification({
            type: 'APPOINTMENT_REMINDER',
            title: 'Appointment Reminder',
            message: `${appointment.patient?.name} has an appointment at ${new Date(appointment.appointmentTime).toLocaleTimeString()}`,
            data: { appointmentId: appointment.id, patientId: appointment.patientId },
            persistent: true
        });
    }, [addNotification]);

    const notifyPatientWaiting = useCallback((patient) => {
        addNotification({
            type: 'PATIENT_WAITING',
            title: 'Patient Waiting',
            message: `${patient.name} is waiting for triage`,
            data: { patientId: patient.id },
            persistent: true
        });
    }, [addNotification]);

    const notifyPrescriptionReady = useCallback((prescription) => {
        addNotification({
            type: 'PRESCRIPTION_READY',
            title: 'Prescription Ready',
            message: `Prescription for ${prescription.patient?.name} is ready for dispensing`,
            data: { prescriptionId: prescription.id, patientId: prescription.patientId },
            persistent: true
        });
    }, [addNotification]);

    const notifyUrgentAlert = useCallback((title, message, data = {}) => {
        addNotification({
            type: 'URGENT_ALERT',
            title,
            message,
            data,
            persistent: true,
            showBrowserNotification: true
        });
    }, [addNotification]);

    const notifySuccess = useCallback((title, message, data = {}) => {
        addNotification({
            type: 'SUCCESS',
            title,
            message,
            data,
            persistent: false
        });
    }, [addNotification]);

    const notifyInfo = useCallback((title, message, data = {}) => {
        addNotification({
            type: 'SYSTEM_INFO',
            title,
            message,
            data,
            persistent: false
        });
    }, [addNotification]);

    // Auto-request notification permission on mount
    useEffect(() => {
        requestNotificationPermission();
    }, [requestNotificationPermission]);

    const value = {
        notifications,
        unreadCount,
        showNotificationPanel,
        setShowNotificationPanel,
        addNotification,
        removeNotification,
        markAsRead,
        markAllAsRead,
        clearAll,
        requestNotificationPermission,
        notifyAppointmentReminder,
        notifyPatientWaiting,
        notifyPrescriptionReady,
        notifyUrgentAlert,
        notifySuccess,
        notifyInfo,
        NOTIFICATION_TYPES
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

// Notification Bell Component
export const NotificationBell = () => {
    const { 
        unreadCount, 
        showNotificationPanel, 
        setShowNotificationPanel 
    } = useNotifications();

    return (
        <div className="relative">
            <button
                onClick={() => setShowNotificationPanel(!showNotificationPanel)}
                className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
                <Bell className="w-6 h-6" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>
        </div>
    );
};

// Notification Panel Component
export const NotificationPanel = () => {
    const {
        notifications,
        showNotificationPanel,
        setShowNotificationPanel,
        markAsRead,
        markAllAsRead,
        clearAll,
        removeNotification,
        NOTIFICATION_TYPES
    } = useNotifications();

    if (!showNotificationPanel) return null;

    const formatTime = (timestamp) => {
        const now = new Date();
        const diff = now - timestamp;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        return `${days}d ago`;
    };

    return (
        <div className="absolute right-0 top-12 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 z-50 max-h-96 overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
                <div className="flex gap-2">
                    {notifications.length > 0 && (
                        <>
                            <button
                                onClick={markAllAsRead}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                            >
                                Mark all read
                            </button>
                            <button
                                onClick={clearAll}
                                className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                            >
                                Clear all
                            </button>
                        </>
                    )}
                </div>
            </div>
            
            <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                        <p>No notifications</p>
                    </div>
                ) : (
                    notifications.map((notification) => {
                        const config = NOTIFICATION_TYPES[notification.type] || NOTIFICATION_TYPES.SYSTEM_INFO;
                        const IconComponent = config.icon;
                        
                        return (
                            <div
                                key={notification.id}
                                className={`p-4 border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                                    !notification.read ? 'bg-blue-50 dark:bg-blue-900' : ''
                                }`}
                                onClick={() => {
                                    if (!notification.read) {
                                        markAsRead(notification.id);
                                    }
                                }}
                            >
                                <div className="flex items-start gap-3">
                                    <div className={`p-2 rounded-full ${config.color}`}>
                                        <IconComponent className={`w-4 h-4 ${config.iconColor}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start">
                                            <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                                                {notification.title}
                                            </h4>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeNotification(notification.id);
                                                }}
                                                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 ml-2"
                                            >
                                                ×
                                            </button>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                            {notification.message}
                                        </p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                            {formatTime(notification.timestamp)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};
